import type { ReactNode } from 'react';
import React, { useCallback, useMemo } from 'react';
import { DeferredRender, RenderMode } from 'app/shared/modules/DeferredRenderWrapper';

interface SafeDeferredRenderProps {
  mode?: RenderMode;
  visibilityOptions?: {
    rootMargin?: string;
    threshold?: number | Array<number>;
    root?: Element | Document;
  };
  children: ReactNode;
}

/**
 * SafeDeferredRender - A wrapper around DeferredRender that validates children
 * to prevent "Objects are not valid as a React child" errors.
 *
 * This component filters out invalid React children before passing them to
 * the DeferredRender library, which has known issues with object handling
 * during SSR/hydration.
 */
const SafeDeferredRender: React.FC<SafeDeferredRenderProps> = ({ mode, visibilityOptions, children }) => {
  const validateAndCleanChildren = useCallback((children: ReactNode): Array<React.ReactElement> => {
    try {
      // Safely convert children to array
      const childArray = React.Children.toArray(children);

      // Filter out any invalid Reactchildren (objects, null, undefined, etc.)
      const validChildren = childArray.filter((child): child is React.ReactElement => {
        if (React.isValidElement(child)) {
          return true;
        }

        // Log invalid children for debugging in development only
        if (process.env.NODE_ENV !== 'production' && child !== null && child !== undefined) {
          // eslint-disable-next-line no-console
          console.warn('SafeDeferredRender: Filtered out invalid child:', {
            child,
            type: typeof child,
            isObject: typeof child === 'object',
          });
        }

        return false;
      });

      return validChildren;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('SafeDeferredRender: Error validating children:', error);
      }
      return [];
    }
  }, []);

  const safeChildren = useMemo(() => validateAndCleanChildren(children), [children]);

  try {
    return (
      <DeferredRender mode={mode} visibilityOptions={visibilityOptions}>
        {safeChildren}
      </DeferredRender>
    );
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('SafeDeferredRender: Error during render, falling back to direct rendering:', error);
    }
    return <>{safeChildren}</>;
  }
};

export default SafeDeferredRender;
export { RenderMode };
